import { metersToMiles, secondsToMinutes } from '@/lib/utils';
import type { RouteCalculation, PricingTier } from '@/types';

// Cloud Run API endpoint
const API_URL = 'https://shuttlebee-api-yiaqzqik6q-uc.a.run.app';

// Helper function to call Cloud Run API
async function callAPI(endpoint: string, data: any): Promise<any> {
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`API call failed: ${response.statusText}`);
  }

  return response.json();
}

// Calculate route between two addresses using Cloud Run API
export async function calculateRoute(
  origin: string,
  destination: string
): Promise<RouteCalculation> {
  try {
    // First geocode both addresses
    const originResult = await callAPI('/geocodeAddress', { address: origin });
    const destResult = await callAPI('/geocodeAddress', { address: destination });

    if (!originResult.places || originResult.places.length === 0) {
      throw new Error('Origin address not found');
    }
    if (!destResult.places || destResult.places.length === 0) {
      throw new Error('Destination address not found');
    }

    const originLocation = originResult.places[0].location;
    const destLocation = destResult.places[0].location;

    // Get directions
    const directionsResult = await callAPI('/getDirections', {
      origin: { latitude: originLocation.latitude, longitude: originLocation.longitude },
      destination: { latitude: destLocation.latitude, longitude: destLocation.longitude },
    });

    if (directionsResult.routes && directionsResult.routes.length > 0) {
      const route = directionsResult.routes[0];

      return {
        distanceMeters: route.distanceMeters || 0,
        distanceMiles: metersToMiles(route.distanceMeters || 0),
        durationSeconds: parseInt(route.duration?.replace('s', '') || '0'),
        durationMinutes: secondsToMinutes(parseInt(route.duration?.replace('s', '') || '0')),
        polyline: route.polyline,
      };
    } else {
      throw new Error('No routes found');
    }
  } catch (error) {
    console.error('Route calculation error:', error);
    throw new Error('Failed to calculate route. Please check the addresses and try again.');
  }
}

// Pricing configuration
const PRICING_CONFIG = {
  // UBERXL base rate multiplied by 1.1
  baseRateMultiplier: 1.1,
  // Surge pricing factor (can be adjusted)
  surgeFactor: 1.0,
  // Base fare
  baseFare: 5.00,
  // Per mile rate
  perMileRate: 2.50,
  // Per minute rate
  perMinuteRate: 0.40,
  // Student count discounts
  studentDiscounts: {
    1: 1.0,    // Full price for 1 student
    2: 0.95,   // 5% discount for 2 students
    3: 0.90,   // 10% discount for 3 students
    4: 0.85,   // 15% discount for 4 students
    5: 0.80,   // 20% discount for 5 students
    6: 0.75,   // 25% discount for 6 students
    7: 0.70,   // 30% discount for 7 students
    8: 0.65,   // 35% discount for 8 students
    9: 0.60,   // 40% discount for 9 students
  } as Record<number, number>,
  // Minimum fare per trip
  minimumFare: 15.00,
  // Minimum price per week per student
  minimumWeeklyRate: 165.00,
};

// Calculate pricing tiers based on route
export function calculatePricingTiers(route: RouteCalculation): PricingTier[] {
  const { distanceMiles, durationMinutes } = route;

  // Add 5 minutes for student loading
  const adjustedDuration = durationMinutes + 5;

  // Determine pricing based on duration
  let pricePerWeekPerStudent: number;

  if (adjustedDuration <= 20) {
    // Short routes: $75/week/student (but enforce minimum)
    pricePerWeekPerStudent = Math.max(75, PRICING_CONFIG.minimumWeeklyRate);
  } else if (adjustedDuration <= 44) {
    // Medium routes: $165/week/student
    pricePerWeekPerStudent = 165;
  } else {
    // Long routes: Dynamic UberXL-style pricing
    // Apply surge pricing for 8AM and 3PM peak times
    const surgeFactor = 1.2; // 20% surge for peak hours

    const distanceCost = distanceMiles * PRICING_CONFIG.perMileRate;
    const timeCost = adjustedDuration * PRICING_CONFIG.perMinuteRate;
    const baseTripPrice = Math.max(
      (PRICING_CONFIG.baseFare + distanceCost + timeCost) *
      PRICING_CONFIG.baseRateMultiplier *
      surgeFactor,
      PRICING_CONFIG.minimumFare
    );

    // Calculate per week (2 trips/day x 5 days = 10 trips)
    pricePerWeekPerStudent = Math.round(baseTripPrice * 10 * 100) / 100;

    // Enforce minimum weekly rate
    pricePerWeekPerStudent = Math.max(pricePerWeekPerStudent, PRICING_CONFIG.minimumWeeklyRate);
  }

  // For routes >44 minutes, calculate tiers for 1-9 students
  // For shorter routes, only calculate 1-6 students
  const maxStudents = adjustedDuration > 44 ? 9 : 6;
  const studentCounts = Array.from({ length: maxStudents }, (_, i) => i + 1);

  // Calculate tiers with discounts
  const tiers: PricingTier[] = studentCounts.map((students) => {
    const discount = PRICING_CONFIG.studentDiscounts[students] || 1.0;
    const pricePerWeek = Math.round(pricePerWeekPerStudent * students * discount * 100) / 100;
    const pricePerTrip = Math.round((pricePerWeek / 10) * 100) / 100;

    return {
      students,
      pricePerTrip,
      pricePerWeek,
      pricePerMonth: Math.round(pricePerWeek * 4 * 100) / 100,
    };
  });

  return tiers;
}

// Calculate custom quote price
export async function calculateCustomQuotePrice(
  pickupAddress: string,
  dropoffAddress: string,
  numberOfStudents: number,
  numberOfWeeks: number
): Promise<{
  route: RouteCalculation;
  tier: PricingTier;
  totalPrice: number;
}> {
  // Calculate route
  const route = await calculateRoute(pickupAddress, dropoffAddress);

  // Get pricing tiers
  const tiers = calculatePricingTiers(route);

  // Find the tier for the given number of students
  const tier = tiers.find((t) => t.students === numberOfStudents) || tiers[0];

  // Calculate total price
  const totalPrice = tier.pricePerWeek * numberOfWeeks;

  return {
    route,
    tier,
    totalPrice,
  };
}

// Geocode address to coordinates using Cloud Run API
export async function geocodeAddress(
  address: string
): Promise<{ lat: number; lng: number } | null> {
  try {
    const result = await callAPI('/geocodeAddress', { address });

    if (result.places && result.places.length > 0) {
      const location = result.places[0].location;
      return {
        lat: location.latitude,
        lng: location.longitude,
      };
    }
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
}

// Get place predictions for autocomplete using Cloud Run API
export async function getPlacePredictions(
  input: string
): Promise<Array<{ description: string; place_id: string }>> {
  try {
    const result = await callAPI('/placeAutocomplete', { input });

    if (result.suggestions) {
      return result.suggestions.map((suggestion: any) => ({
        description: suggestion.placePrediction?.text?.text || suggestion.text,
        place_id: suggestion.placePrediction?.placeId || suggestion.placeId,
      }));
    }
    return [];
  } catch (error) {
    console.error('Autocomplete error:', error);
    return [];
  }
}

// Get place details using Cloud Run API
export async function getPlaceDetails(placeId: string): Promise<any> {
  try {
    const result = await callAPI('/placeDetails', { placeId });
    return result;
  } catch (error) {
    console.error('Place details error:', error);
    return null;
  }
}

// Export pricing config for use elsewhere
export { PRICING_CONFIG };
